import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useSmartPrefetch } from '@/hooks/useSmartPrefetch';
import ImpersonationBanner from '@/components/common/ImpersonationBanner';
import { 
  LayoutDashboard, 
  Calendar, 
  CalendarRange,
  Users, 
  CreditCard, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  MapPin,
  Settings,
  Scissors,
  ShoppingBag,
  ClipboardList,
  PiggyBank,
  BarChart3,
  Smartphone,
  TrendingUp,
  UserCog,
  Stethoscope,
  Shield,
  FileHeart,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Activity,
  Star,
  UserCircle,
  CalendarDays,
  Package,
  Images,
  UserCheck,
  Brain,
  Calculator,
  ListChecks,
  Tablet,
  Monitor,
  Moon,
  Sparkles,
  Megaphone,
  Building2,
  Zap,
  QrCode,
  MessageCircle,
  Euro,
  Handshake,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import NotificationBell from '@/components/admin/NotificationBell';
import SynologyAlertBadge from '@/components/admin/SynologyAlertBadge';
import WaitingRoomBadge from '@/components/admin/WaitingRoomBadge';
import OfflineIndicator from '@/components/common/OfflineIndicator';
import KeyboardShortcuts from '@/components/shared/KeyboardShortcuts';
import PatientQuickViewDialog from '@/components/admin/PatientQuickViewDialog';
import api, { waitlistAPI } from '@/lib/api';
import { toast } from 'sonner';
import { playNotificationChime } from '@/lib/notificationSound';

const getNavItems = (isAdmin, isDoctor, can, isSuperAdmin) => {
  const items = [];
  
  // Dashboard - visible for admin and doctors (each sees their own dashboard)
  if (isAdmin || isDoctor) {
    items.push({ path: '/admin/dashboard', label: isDoctor ? 'Mi Panel' : 'Dashboard', icon: LayoutDashboard });
  }
  
  // Always visible (admin, recepcionista, enfermería, doctor)
  items.push(
    { path: '/admin/agenda', label: 'Agenda', icon: Calendar },
  );

  // Sala de espera del Área Paciente (check-in en vivo + "pasar a
  // consulta") — justo debajo de Agenda para acceso rápido.
  // Registrada en `adminPages.js` como `waiting_room`.
  if (can('admin_pages', 'waiting_room')) {
    items.push({ path: '/admin/sala-espera', label: 'Sala de espera', icon: UserCheck });
  }

  items.push(
    { path: '/admin/weekly-summary', label: 'Resumen Semanal', icon: CalendarRange },
    { path: '/admin/appointments', label: 'Citas', icon: Users },
    { path: '/admin/waitlist', label: 'Lista de Espera', icon: ListChecks },
    { path: '/admin/patients', label: 'Pacientes', icon: UserCog },
  );
  
  // Historia Clínica - needs permission (shared for doctors)
  if (can('clinical_history', 'view')) {
    items.push({ path: '/admin/clinical-history', label: 'Historia Clínica', icon: FileHeart });
  }
  
  // Cirugías - shared for all (doctors can see surgical schedule)
  items.push(
    { path: '/admin/surgeries', label: 'Cirugías', icon: Scissors },
    { path: '/admin/planning', label: 'Planificación', icon: CalendarDays },
    { path: '/admin/presupuestos', label: 'Presupuestos', icon: Calculator },
  );

  // Casuística clínica — superadmin/admin ven todo, cada doctor lo suyo
  if (isAdmin || isDoctor) {
    items.push({ path: '/admin/casuistica', label: 'Casuística', icon: Activity });
  }
  // Cobros Stripe — admin_pages registry (superadmin siempre; resto sólo con toggle)
  if (can('admin_pages', 'stripe_payments')) {
    items.push({ path: '/admin/pagos-stripe', label: 'Honorarios Pago Personal', icon: CreditCard });
  }
  // Sujetadores postoperatorios — sólo superadmin (registry admin_pages)
  if (can('admin_pages', 'bra_registry')) {
    items.push({ path: '/admin/sujetadores', label: 'Sujetadores', icon: ShoppingBag });
  }
  
  // Procedimientos (precios) - needs prices permission
  if (can('prices', 'view')) {
    items.push({ path: '/admin/procedures', label: 'Procedimientos', icon: ClipboardList });
  }
  // Cirujanos Colaboradores — admin_pages registry (superadmin siempre)
  if (can('admin_pages', 'collaborating_surgeons')) {
    items.push({ path: '/admin/collaborating-surgeons', label: 'Cirujanos Colaboradores', icon: UserCog });
  }

  // Prótesis de Mama (antes Catálogo Motiva) - visible for admin and doctors
  if (isAdmin || isDoctor) {
    items.push({ path: '/admin/protesis-mama', label: 'Prótesis de Mama', icon: Package });
  }

  // Galería antes/después — admin+superadmin por defecto; gobernable
  // desde el editor de permisos (`admin_pages.gallery`).
  if (can('admin_pages', 'gallery')) {
    items.push({ path: '/admin/gallery', label: 'Galería Antes/Después', icon: Images });
  }

  // Sala de espera — movida arriba, justo debajo de Agenda (jun-2026).

  // Triajes IA de urgencias — bandeja donde caen las solicitudes de
  // "Cita urgente" del portal paciente clasificadas por Claude.
  // Gobernable desde el editor de permisos (`admin_pages.urgent_triage_inbox`).
  if (can('admin_pages', 'urgent_triage_inbox')) {
    items.push({ path: '/admin/triage', label: 'Triajes urgentes', icon: Zap });
  }

  // WhatsApp Clínica — conversaciones con pacientes + asistente 24/7.
  if (can('admin_pages', 'whatsapp_clinic')) {
    items.push({ path: '/admin/whatsapp', label: 'WhatsApp Clínica', icon: MessageCircle });
  }

  // Pacientes auto-registrados pendientes de verificación por recepción.
  // El endpoint `/api/admin/patient-verification/stats` alimenta un badge
  // numérico. Silencioso cuando no hay pendientes.
  if (can('admin_pages', 'patient_verification')) {
    items.push({ path: '/admin/patient-verification', label: 'Verificar altas', icon: ShieldCheck, badgeKey: 'pending_verifications' });
  }

  // QR imprimible que apunta a `/mi-area/registro` — para colgar en la
  // sala de espera o incluir en flyers/redes.
  if (can('admin_pages', 'registration_qr')) {
    items.push({ path: '/admin/qr-registro', label: 'QR de captación', icon: QrCode });
  }

  // Estadísticas de documentos compartidos con pacientes (Área Paciente).
  if (can('admin_pages', 'sharing_stats')) {
    items.push({ path: '/admin/patient-app', label: 'App Paciente', icon: Smartphone });
    items.push({ path: '/admin/testimonios', label: 'Testimonios', icon: Star });
  }

  // Campaña de anuncio del Área Paciente.
  if (can('admin_pages', 'portal_announcement')) {
    items.push({ path: '/admin/anuncio-area-paciente', label: 'Anuncio Área Paciente', icon: Megaphone });
  }

  // Campaña "Aviso La Salud" — transición de pacientes del IMED.
  if (can('admin_pages', 'clinic_move_campaign')) {
    items.push({ path: '/admin/aviso-la-salud', label: 'Aviso La Salud', icon: Building2 });
  }
  
  // Ubicaciones — admin_pages registry (superadmin siempre pasa por
  // el short-circuit `isAdmin` en `can`; otros roles sólo si el
  // superadmin ha activado el toggle en su editor de permisos).
  if (can('admin_pages', 'locations')) {
    items.push(
      { path: '/admin/locations', label: 'Ubicaciones', icon: MapPin },
    );
  }
  
  // Aseguradoras - visible for admin and doctors
  if (isAdmin || isDoctor) {
    items.push(
      { path: '/admin/insurances', label: 'Aseguradoras', icon: CreditCard },
    );
  }
  
  // TuoTempo — admin_pages registry
  if (can('admin_pages', 'tuotempo')) {
    items.push(
      { path: '/admin/tuotempo', label: 'TuoTempo Sync', icon: RefreshCw },
    );
  }
  
  // Billing - needs permission
  if (can('billing', 'view')) {
    items.push({ path: '/admin/billing', label: 'Facturación', icon: FileText });
  }
  
  // Expenses - needs billing permission
  if (can('billing', 'view')) {
    items.push({ path: '/admin/expenses', label: 'Gastos', icon: PiggyBank });
  }
  // Ayudantías — auditoría de pagos quirúrgicos, SOLO superadmin
  if (isSuperAdmin) {
    items.push({ path: '/admin/ayudantias', label: 'Ayudantías', icon: Handshake });
  }
  // Auditoría Facturación — admin_pages registry
  if (can('admin_pages', 'billing_audit')) {
    items.push({ path: '/admin/billing-audit', label: 'Auditoría Facturación', icon: ClipboardList });
  }
  // Informe honorarios colaboradores — admin_pages registry
  if (can('admin_pages', 'collaborating_surgeons_report')) {
    items.push({ path: '/admin/collaborating-surgeons/report', label: 'Honorarios Colaboradores', icon: Euro });
  }
  
  // Stats / Reviews — admin_pages registry
  if (can('admin_pages', 'stats')) {
    items.push({ path: '/admin/stats', label: 'Estadísticas', icon: BarChart3 });
  }
  if (can('admin_pages', 'reviews')) {
    items.push({ path: '/admin/reviews', label: 'Reseñas', icon: Star });
  }
  
  // Analytics - visible for admin and doctors with billing access
  if (isAdmin || (isDoctor && can('billing', 'view'))) {
    items.push({ path: '/admin/analytics', label: 'Analytics', icon: TrendingUp });
  }
  
  // Signature History - visible for admin and doctors
  if (isAdmin || isDoctor) {
    items.push({ path: '/admin/signature-history', label: isDoctor ? 'Mis Firmas' : 'Historial Firmas', icon: ShieldCheck });
  }
  
  // Monitoring - needs permission
  if (can('monitoring', 'view') || can('admin_pages', 'monitoring')) {
    items.push({ path: '/admin/monitoring', label: 'Monitoreo', icon: Activity });
  }
  
  // Admin pages driven by the central registry (superadmin sees them by
  // default; other roles only when the individual toggle is on).
  if (can('admin_pages', 'doctors_management')) {
    items.push({ path: '/admin/doctors', label: 'Gestión Doctores', icon: Stethoscope });
  }
  if (can('admin_pages', 'ai_usage')) {
    items.push({ path: '/admin/ai-usage', label: 'Uso de IA', icon: Brain });
  }
  if (can('admin_pages', 'kiosk_devices')) {
    items.push({ path: '/admin/kiosk-devices', label: 'Tablets de firma', icon: Tablet });
  }
  if (can('admin_pages', 'tv_banner')) {
    items.push({ path: '/admin/tv-banner', label: 'TV sala de espera', icon: Monitor });
  }
  if (can('admin_pages', 'whatsapp_quiet_hours')) {
    items.push({ path: '/admin/whatsapp-quiet-hours', label: 'WhatsApp · Horas silencio', icon: Moon });
  }

  // Settings / Users / Compliance / RGPD — hard superadmin gate (NO
  // están en el registro ADMIN_PAGES a propósito: son áreas
  // administrativas críticas que no deben poderse delegar por
  // toggle).
  if (isSuperAdmin) {
    items.push(
      { path: '/admin/settings', label: 'Configuración', icon: Settings },
      { path: '/admin/users', label: 'Usuarios', icon: Shield },
      { path: '/admin/compliance', label: 'Cumplimiento RGPD', icon: ShieldCheck },
      { path: '/admin/rgpd-breaches', label: 'Brechas RGPD', icon: AlertTriangle },
      { path: '/admin/klinilog-leads', label: 'Leads Klinilog', icon: Sparkles },
      { path: '/admin/costes-programa', label: 'Costes del Programa', icon: Wrench },
    );
  }

  // Mi Perfil - visible for all authenticated users
  items.push(
    { path: '/admin/profile', label: 'Mi Perfil', icon: UserCircle },
    { path: '/admin/novedades', label: 'Novedades', icon: Sparkles },
  );
  
  return items;
};

export const AdminLayout = () => {
  const { admin, logout, isAdmin, isSuperAdmin, isDoctor } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [waitlistPending, setWaitlistPending] = useState(0);
  const [klinilogLeadsUnread, setKlinilogLeadsUnread] = useState(0);
  const [verificationsPending, setVerificationsPending] = useState(0);

  // Precarga silenciosa de endpoints hot (agenda, pacientes, presupuestos…)
  // en background — el SW los cacheará, así navegar entre secciones se
  // siente instantáneo y funciona offline si se cae la red después.
  useSmartPrefetch();

  // Poll the count every 60s so the badge stays fresh without overloading the
  // API. We also refresh on tab focus so the recepcionista sees changes
  // immediately after returning to the app.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await waitlistAPI.pendingCount();
        if (!cancelled) setWaitlistPending(r.data?.pending || 0);
      } catch (_) { /* silent — badge just hides */ }
    };
    load();
    const interval = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Poll the Klinilog leads unread badge (status="new") for superadmins only.
  // Same cadence as waitlist (60s + on focus) + a custom event so the leads
  // page can force-refresh the badge after marking a lead as contacted.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/klinilog/leads/unread-count');
        if (!cancelled) setKlinilogLeadsUnread(data?.unread || 0);
      } catch (_) { /* silent — badge just hides */ }
    };
    load();
    const interval = setInterval(load, 60_000);
    const onFocus = () => load();
    const onRefresh = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('klinilog-leads:refresh', onRefresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('klinilog-leads:refresh', onRefresh);
    };
  }, [isSuperAdmin]);

  // Poll verificaciones pendientes (auto-registros) cada 60s + on focus.
  // Se refresca también al recibir el evento custom `patient-verification:refresh`
  // desde la propia página de bandeja tras verify/reject.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/admin/patient-verification/stats');
        if (!cancelled) setVerificationsPending(data?.pending || 0);
      } catch (_) { /* silent */ }
    };
    load();
    const interval = setInterval(load, 60_000);
    const onFocus = () => load();
    const onRefresh = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('patient-verification:refresh', onRefresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('patient-verification:refresh', onRefresh);
    };
  }, []);

  // Global agenda SSE listener:
  //   • Shows a floating toast with the patient's action (configurable via
  //     `floating_notifications` flag in localStorage; default ON).
  //   • Emits a `agenda:event` window event so any open dashboard / agenda /
  //     bell can refetch instantly.
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    if (!apiUrl || !token) return;

    let es = null;
    try {
      es = new EventSource(`${apiUrl}/api/agenda/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        if (!ev.data || ev.data === '{}') return;
        let payload;
        try { payload = JSON.parse(ev.data); } catch (_) { return; }

        // Re-broadcast inside the SPA so other components can refetch.
        window.dispatchEvent(new CustomEvent('agenda:event', { detail: payload }));

        // Floating toast — opt-out via localStorage flag.
        const floatingEnabled = localStorage.getItem('floating_notifications') !== 'false';
        if (!floatingEnabled) return;
        if (payload.type === 'appointment_updated') {
          const verb = ({
            confirm: 'ha confirmado',
            cancel: 'ha cancelado',
            reschedule: 'ha pedido cambiar',
          })[payload.action] || 'ha actualizado';
          const when = payload.date && payload.start_time
            ? ` (${payload.date} a las ${payload.start_time})`
            : '';
          const tone = payload.action === 'cancel' ? 'warning' : 'success';
          const message = `${payload.patient_name || 'Un paciente'} ${verb} su cita${when}`;
          if (tone === 'warning') toast.warning(message);
          else toast.success(message);
          // Sutil "ding" — solo si el usuario lo ha activado en su perfil
          // (default OFF para no asustar a nadie la primera vez).
          if (localStorage.getItem('notification_sound') === 'true') {
            playNotificationChime();
          }
        }
      };
      es.onerror = () => {
        if (es?.readyState === 2) { try { es.close(); } catch (_) {} }
      };
    } catch (_) { /* ignore — server-side or auth issue */ }

    return () => { if (es) { try { es.close(); } catch (_) {} } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = getNavItems(isAdmin, isDoctor, can, isSuperAdmin);

  const getRoleBadge = () => {
    if (isSuperAdmin) return { label: 'Superadmin', className: 'bg-brand-navy' };
    if (isAdmin) return { label: 'Admin', className: 'bg-brand-navy' };
    if (isDoctor) return { label: 'Doctor', className: 'bg-emerald-600' };
    return { label: 'Usuario', className: 'bg-slate-600' };
  };
  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen bg-blush-light/25 flex flex-col">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50
        w-64 bg-brand-navy
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo — safe-area para que el botón X no quede bajo el notch */}
          <div
            className="p-6 border-b border-white/10"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--ios-blur-gap, 0px) + 1.5rem)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-white font-heading text-lg leading-snug">Unidad de Mama<br />Dr. Marcos Adrianzén</h1>
                <p className="text-white/60 text-sm">Panel de Administración</p>
              </div>
              <button 
                className="lg:hidden text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => {
                const isWaitlist = item.path === '/admin/waitlist';
                const isKlinilogLeads = item.path === '/admin/klinilog-leads';
                const isVerifications = item.path === '/admin/patient-verification';
                const showWaitlistBadge = isWaitlist && waitlistPending > 0;
                const showKlinilogBadge = isKlinilogLeads && klinilogLeadsUnread > 0;
                const showVerificationsBadge = isVerifications && verificationsPending > 0;
                const showBadge = showWaitlistBadge || showKlinilogBadge || showVerificationsBadge;
                const badgeCount = showWaitlistBadge
                  ? waitlistPending
                  : showKlinilogBadge
                    ? klinilogLeadsUnread
                    : verificationsPending;
                const badgeTitle = showWaitlistBadge
                  ? `${waitlistPending} paciente(s) notificado(s) sin reservar`
                  : showKlinilogBadge
                    ? `${klinilogLeadsUnread} lead(s) nuevo(s) sin contactar`
                    : `${verificationsPending} alta(s) de paciente pendiente(s) de verificar`;
                const badgeTestId = showWaitlistBadge
                  ? 'waitlist-pending-badge'
                  : showKlinilogBadge
                    ? 'klinilog-leads-unread-badge'
                    : 'verifications-pending-badge';
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      sidebar-link
                      ${isActive ? 'active' : ''}
                    `}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                    {showBadge && (
                      <Badge
                        className="ml-auto bg-red-600 hover:bg-red-600 text-white border-0 min-w-[1.5rem] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold"
                        data-testid={badgeTestId}
                        title={badgeTitle}
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </Badge>
                    )}
                    {!showBadge && <ChevronRight size={16} className="ml-auto opacity-50" />}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-blush-light flex items-center justify-center">
                <span className="text-brand-navy font-semibold">
                  {admin?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {admin?.name || 'Admin'}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-white/60 text-xs truncate">
                    {admin?.email || ''}
                  </p>
                </div>
                <Badge
                  data-testid="role-badge"
                  className={`text-xs mt-1 ${roleBadge.className}`}
                >
                  {roleBadge.label}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut size={18} className="mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Top bar — sticky y con safe-area para que el menú hamburguesa
            no quede bajo la barra de estado del iPhone (PWA standalone
            con viewport-fit=cover) y siga accesible al hacer scroll. */}
        <header
          className="bg-white border-b border-gray-200 px-4 pb-3 lg:px-6 sticky top-0 z-30"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--ios-blur-gap, 0px) + 0.75rem)' }}
        >
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} className="text-brand-navy" />
            </button>
            <div className="flex-1">
              <a 
                href="https://www.drmarcosadrianzen.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-brand-navy hover:underline"
              >
                www.drmarcosadrianzen.com
              </a>
            </div>
            <NotificationBell />
            <WaitingRoomBadge />
            <SynologyAlertBadge />
            <OfflineIndicator />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-w-0 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      </div>
      {/* Global keyboard shortcuts + universal patient search + cheatsheet */}
      <KeyboardShortcuts />
      {/* Ficha rápida de paciente en overlay (openPatientQuickView) */}
      <PatientQuickViewDialog />
    </div>
  );
};

export default AdminLayout;
